import {
  Badge,
  Button,
  buttonVariants,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Dialog,
  DialogContent,
  EmptyState,
  Field,
  FieldDescription,
  FieldLabel,
  Input,
  inputClassName,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  Spinner,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  useToast,
} from "@forge/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowLeft, Cable, Copy, RefreshCw } from "lucide-react";
import { useEffect, useState } from "react";
import { PageHeader } from "../../components/page-header";
import { api, unwrap } from "../../lib/api";
import { formatDateTime } from "../../lib/format";

export const Route = createFileRoute("/_app/connections_/inbound")({
  component: IntegrationsPage,
});

const NONE = "__none__";
const ROOT = "__root__";

type Mapping = {
  collectionPath: string;
  sku: string;
  quantity?: string | null;
  cost?: string | null;
};

type FieldOption = { path: string; sample: string };

type PreviewRow =
  | { ok: true; sku: string; quantity: number | null; cost: string | null }
  | { ok: false; reason: string };

type DiffItem = {
  sku: string;
  status: "update" | "unchanged" | "unknown" | "invalid";
  quantity?: { from: number | null; to: number | null };
  cost?: { from: string | null; to: string | null };
  reason?: string;
};

function apiErrorMessage(error: unknown, fallback: string) {
  if (error instanceof Error && error.message) return error.message;
  if (error && typeof error === "object" && "value" in error) {
    const value = (error as { value: unknown }).value;
    if (
      value &&
      typeof value === "object" &&
      "error" in value &&
      typeof (value as { error: unknown }).error === "string"
    ) {
      return (value as { error: string }).error;
    }
  }
  return fallback;
}

function pathValue(path: string | null | undefined, empty = NONE) {
  return path ? path : empty;
}

function fromPathValue(value: unknown, empty = NONE) {
  const path = typeof value === "string" ? value : "";
  return path === empty ? "" : path;
}

async function copyText(value: string) {
  await navigator.clipboard.writeText(value);
}

function IntegrationsPage() {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [revealedToken, setRevealedToken] = useState<string | null>(null);

  const list = useQuery({
    queryKey: ["integrations"],
    queryFn: async () => unwrap(await api.integrations.get()),
  });

  const create = useMutation({
    mutationFn: async () => unwrap(await api.integrations.post({ name: "ERP" })),
    onSuccess: (source) => {
      setRevealedToken(source.token);
      void queryClient.invalidateQueries({ queryKey: ["integrations"] });
    },
    onError: (error) => {
      toast.add({
        type: "error",
        title: "Não foi possível criar a entrada",
        description: apiErrorMessage(error, "Tente de novo."),
      });
    },
  });

  const source = list.data?.sources[0] ?? null;

  return (
    <>
      <PageHeader
        parent={
          <Link
            to="/connections"
            className="text-2xs font-medium text-ink-muted transition-colors duration-100 hover:text-ink"
          >
            Conexões
          </Link>
        }
        title="Entrada do ERP"
        description="O ERP manda estoque, custo e, na Shopee, o XML da NF-e. O Bridge repassa para o canal."
        actions={
          <Link
            to="/connections"
            className={buttonVariants({ variant: "secondary", size: "sm" })}
          >
            <ArrowLeft />
            Conexões
          </Link>
        }
      />

      {list.isLoading ? (
        <div className="flex justify-center py-16">
          <Spinner className="size-5 text-ink-muted" />
        </div>
      ) : !source ? (
        <Card>
          <EmptyState
            icon={<Cable />}
            title="Nenhuma entrada ainda"
            description="Cole um JSON do seu sistema, mapeie SKU, quantidade e custo, e veja o diff antes de ativar. Seu sistema só precisa do token."
            action={
              <Button loading={create.isPending} onClick={() => create.mutate()}>
                Criar entrada
              </Button>
            }
          />
        </Card>
      ) : (
        <SourceEditor
          source={source}
          inboundUrl={list.data?.inboundUrl ?? source.inboundUrl}
          runs={list.data?.runs ?? []}
          onRevealToken={setRevealedToken}
        />
      )}

      <Dialog
        open={revealedToken != null}
        onOpenChange={(open) => {
          if (!open) setRevealedToken(null);
        }}
      >
        <DialogContent
          title="Guarde este token"
          description="Ele não aparece de novo. Quem tiver o token escreve estoque e custo nesta organização."
        >
          <div className="flex flex-col gap-3">
            <code className="break-all rounded-control bg-surface-sunken px-2.5 py-2 font-mono text-2xs text-ink">
              {revealedToken}
            </code>
            <Button
              variant="secondary"
              onClick={() => {
                if (!revealedToken) return;
                void copyText(revealedToken).then(() => {
                  toast.add({ type: "success", title: "Token copiado" });
                });
              }}
            >
              <Copy />
              Copiar token
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

type Source = {
  id: string;
  name: string;
  tokenPrefix: string;
  mapping: Mapping | null;
  samplePayload: unknown;
  enabled: boolean;
  ready: boolean;
  inboundUrl: string;
  lastDryRunAt: string | Date | null;
  lastAppliedAt: string | Date | null;
};

type Run = {
  id: string;
  mode: string;
  summary: string;
  received: number;
  update: number;
  unknown: number;
  createdAt: string | Date;
};

function SourceEditor({
  source,
  inboundUrl,
  runs,
  onRevealToken,
}: {
  source: Source;
  inboundUrl: string;
  runs: Run[];
  onRevealToken: (token: string | null) => void;
}) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [name, setName] = useState(source.name);
  const [raw, setRaw] = useState(() =>
    source.samplePayload != null
      ? JSON.stringify(source.samplePayload, null, 2)
      : "",
  );
  const [parseError, setParseError] = useState<string | null>(null);
  const [collections, setCollections] = useState<FieldOption[]>([]);
  const [fields, setFields] = useState<FieldOption[]>([]);
  const [mapping, setMapping] = useState<Mapping>(
    source.mapping ?? { collectionPath: "", sku: "", quantity: null, cost: null },
  );
  const [preview, setPreview] = useState<PreviewRow[]>([]);
  const [diff, setDiff] = useState<{
    summary: string;
    counts: {
      update: number;
      unchanged: number;
      unknown: number;
      invalid: number;
    };
    items: DiffItem[];
  } | null>(null);

  useEffect(() => {
    setName(source.name);
    setMapping(
      source.mapping ?? {
        collectionPath: "",
        sku: "",
        quantity: null,
        cost: null,
      },
    );
  }, [source.id, source.name, source.mapping]);

  const inspect = useMutation({
    mutationFn: async (next: Mapping) => {
      const payload = JSON.parse(raw) as unknown;
      return unwrap(
        await api.integrations.inspect.post({ payload, mapping: next }),
      );
    },
    onSuccess: (data) => {
      setParseError(null);
      setCollections(data.collections);
      setFields(data.fields);
      setMapping(data.mapping);
      setPreview(data.preview);
    },
    onError: (error) => {
      const message =
        error instanceof SyntaxError
          ? "JSON inválido"
          : apiErrorMessage(error, "Não foi possível ler o JSON");
      setParseError(message);
    },
  });

  const save = useMutation({
    mutationFn: async (patch: {
      name?: string;
      mapping?: Mapping;
      samplePayload?: unknown;
      enabled?: boolean;
    }) => unwrap(await api.integrations({ id: source.id }).patch(patch)),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["integrations"] });
    },
    onError: (error) => {
      toast.add({
        type: "error",
        title: "Não foi possível salvar",
        description: apiErrorMessage(error, "Tente de novo."),
      });
    },
  });

  const dryRun = useMutation({
    mutationFn: async () => {
      const payload = JSON.parse(raw) as unknown;
      await unwrap(
        await api.integrations({ id: source.id }).patch({
          mapping,
          samplePayload: payload,
        }),
      );
      return unwrap(
        await api.integrations({ id: source.id })["dry-run"].post({ payload }),
      );
    },
    onSuccess: async (data) => {
      setParseError(null);
      setDiff({
        summary: data.summary,
        counts: data.counts,
        items: data.details ?? data.items ?? [],
      });
      await queryClient.invalidateQueries({ queryKey: ["integrations"] });
    },
    onError: (error) => {
      const message =
        error instanceof SyntaxError
          ? "JSON inválido"
          : apiErrorMessage(error, "Dry-run falhou");
      setParseError(message);
      toast.add({ type: "error", title: message });
    },
  });

  const rotate = useMutation({
    mutationFn: async () =>
      unwrap(await api.integrations({ id: source.id }).rotate.post()),
    onSuccess: (data) => {
      onRevealToken(data.source.token);
      void queryClient.invalidateQueries({ queryKey: ["integrations"] });
    },
  });

  function readFields() {
    inspect.mutate(mapping);
  }

  function changeMapping(next: Mapping) {
    setMapping(next);
    setDiff(null);
    if (raw.trim() && fields.length > 0) {
      inspect.mutate(next);
    }
  }

  const ready = Boolean(mapping.sku && (mapping.quantity || mapping.cost));

  return (
    <div className="flex flex-col gap-4">
      <Card>
        <CardHeader>
          <CardTitle>Endpoint</CardTitle>
          <Badge variant={source.enabled ? "success" : "neutral"} dot>
            {source.enabled ? "Ativa" : "Rascunho"}
          </Badge>
        </CardHeader>
        <CardContent className="flex flex-col gap-3">
          <Field>
            <FieldLabel>Nome</FieldLabel>
            <Input
              value={name}
              onChange={(event) => setName(event.target.value)}
              onBlur={() => {
                if (name.trim() && name !== source.name) {
                  save.mutate({ name });
                }
              }}
            />
          </Field>
          <Field>
            <FieldLabel>POST</FieldLabel>
            <div className="flex gap-2">
              <code className="flex h-8 flex-1 items-center truncate rounded-control bg-surface-sunken px-2.5 font-mono text-2xs text-ink">
                {inboundUrl}
              </code>
              <Button
                variant="secondary"
                size="icon"
                aria-label="Copiar URL"
                onClick={() => {
                  void copyText(inboundUrl).then(() => {
                    toast.add({ type: "success", title: "URL copiada" });
                  });
                }}
              >
                <Copy />
              </Button>
            </div>
            <FieldDescription>
              Authorization: Bearer {source.tokenPrefix}
            </FieldDescription>
          </Field>
          <div className="flex flex-wrap items-center gap-2">
            <Button
              variant="secondary"
              size="sm"
              loading={rotate.isPending}
              onClick={() => rotate.mutate()}
            >
              <RefreshCw />
              Novo token
            </Button>
            <label className="ml-auto flex items-center gap-2 text-2xs text-ink-secondary">
              <Switch
                checked={source.enabled}
                disabled={!ready && !source.enabled}
                onCheckedChange={(checked) => {
                  let samplePayload: unknown;
                  try {
                    samplePayload = raw.trim() ? JSON.parse(raw) : undefined;
                  } catch {
                    samplePayload = undefined;
                  }
                  save.mutate({
                    enabled: checked,
                    mapping,
                    ...(samplePayload !== undefined ? { samplePayload } : {}),
                  });
                }}
              />
              Receber envios
            </label>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>De-para</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-col gap-4">
          <Field>
            <FieldLabel>JSON de exemplo</FieldLabel>
            <textarea
              value={raw}
              onChange={(event) => {
                setRaw(event.target.value);
                setParseError(null);
              }}
              spellCheck={false}
              placeholder='{ "data": [{ "codigo": "SKU-1", "estoque": 10, "custo": 4.5 }] }'
              className={`${inputClassName} h-auto min-h-36 py-2 font-mono text-2xs leading-relaxed`}
            />
            {parseError ? (
              <p className="text-2xs text-danger">{parseError}</p>
            ) : (
              <FieldDescription>
                Cole o que o seu sistema vai mandar. Depois leia os campos e
                aponte cada um.
              </FieldDescription>
            )}
          </Field>

          <div className="flex flex-wrap gap-2">
            <Button
              variant="secondary"
              loading={inspect.isPending}
              disabled={!raw.trim()}
              onClick={readFields}
            >
              Ler campos
            </Button>
            <Button
              loading={dryRun.isPending}
              disabled={!ready || !raw.trim()}
              onClick={() => dryRun.mutate()}
            >
              Dry-run com diff
            </Button>
          </div>

          {fields.length > 0 && (
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <Field>
                <FieldLabel>Lista de registros</FieldLabel>
                <Select
                  value={pathValue(mapping.collectionPath, ROOT)}
                  onValueChange={(value) =>
                    changeMapping({
                      ...mapping,
                      collectionPath: fromPathValue(value, ROOT),
                    })
                  }
                >
                  <SelectTrigger placeholder="Escolher lista" />
                  <SelectContent>
                    {collections.map((item) => (
                      <SelectItem
                        key={item.path || ROOT}
                        value={pathValue(item.path, ROOT)}
                      >
                        {item.path || "payload inteiro"} · {item.sample}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
              <Field>
                <FieldLabel>SKU</FieldLabel>
                <FieldSelect
                  value={mapping.sku}
                  fields={fields}
                  placeholder="Campo do código"
                  onChange={(sku) => changeMapping({ ...mapping, sku })}
                />
              </Field>
              <Field>
                <FieldLabel>Quantidade</FieldLabel>
                <FieldSelect
                  value={mapping.quantity ?? ""}
                  fields={fields}
                  allowNone="Não enviar quantidade"
                  placeholder="Campo da quantidade"
                  onChange={(quantity) =>
                    changeMapping({ ...mapping, quantity: quantity || null })
                  }
                />
              </Field>
              <Field>
                <FieldLabel>Custo</FieldLabel>
                <FieldSelect
                  value={mapping.cost ?? ""}
                  fields={fields}
                  allowNone="Não enviar custo"
                  placeholder="Campo do custo"
                  onChange={(cost) =>
                    changeMapping({ ...mapping, cost: cost || null })
                  }
                />
              </Field>
            </div>
          )}

          {preview.length > 0 && (
            <PreviewTable rows={preview} />
          )}
        </CardContent>
      </Card>

      {diff && <DiffCard diff={diff} />}

      {runs.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Execuções</CardTitle>
          </CardHeader>
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                <TableHead>Quando</TableHead>
                <TableHead>Modo</TableHead>
                <TableHead>Resumo</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {runs.map((run) => (
                <TableRow key={run.id}>
                  <TableCell className="text-ink-muted">
                    {formatDateTime(run.createdAt)}
                  </TableCell>
                  <TableCell>
                    <Badge variant={run.mode === "apply" ? "accent" : "neutral"}>
                      {run.mode === "apply" ? "Aplicado" : "Dry-run"}
                    </Badge>
                  </TableCell>
                  <TableCell>{run.summary}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}
    </div>
  );
}

function FieldSelect({
  value,
  fields,
  onChange,
  placeholder,
  allowNone,
}: {
  value: string;
  fields: FieldOption[];
  onChange: (path: string) => void;
  placeholder: string;
  allowNone?: string;
}) {
  return (
    <Select
      value={pathValue(value)}
      onValueChange={(next) => onChange(fromPathValue(next))}
    >
      <SelectTrigger placeholder={placeholder} />
      <SelectContent>
        {allowNone && <SelectItem value={NONE}>{allowNone}</SelectItem>}
        {fields.map((field) => (
          <SelectItem key={field.path} value={field.path}>
            {field.path}
            <span className="ml-2 text-ink-faint">{field.sample}</span>
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

function PreviewTable({ rows }: { rows: PreviewRow[] }) {
  return (
    <div>
      <p className="mb-2 text-2xs font-medium text-ink-muted">
        Preview dos primeiros registros
      </p>
      <Table>
        <TableHeader>
          <TableRow className="hover:bg-transparent">
            <TableHead>SKU</TableHead>
            <TableHead className="text-right">Qtd</TableHead>
            <TableHead className="text-right">Custo</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((row, index) => (
            <TableRow key={`${index}-${row.ok ? row.sku : row.reason}`}>
              {row.ok ? (
                <>
                  <TableCell className="font-mono text-2xs">{row.sku}</TableCell>
                  <TableCell className="text-right">
                    {row.quantity ?? "—"}
                  </TableCell>
                  <TableCell className="text-right">
                    {row.cost ?? "—"}
                  </TableCell>
                </>
              ) : (
                <TableCell colSpan={3} className="text-danger">
                  {row.reason}
                </TableCell>
              )}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

function DiffCard({
  diff,
}: {
  diff: {
    summary: string;
    counts: {
      update: number;
      unchanged: number;
      unknown: number;
      invalid: number;
    };
    items: DiffItem[];
  };
}) {
  const notable = diff.items.filter((item) => item.status !== "unchanged");
  return (
    <Card>
      <CardContent className="flex flex-col gap-4">
        <div>
          <p className="text-sm font-semibold tracking-tight text-ink">
            {diff.summary}
          </p>
          <div className="mt-3 flex flex-wrap gap-2">
            <CountChip label="Atualizaria" value={diff.counts.update} />
            <CountChip label="Iguais" value={diff.counts.unchanged} />
            <CountChip label="Desconhecidos" value={diff.counts.unknown} />
            {diff.counts.invalid > 0 && (
              <CountChip label="Inválidos" value={diff.counts.invalid} />
            )}
          </div>
        </div>
        {notable.length > 0 && (
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                <TableHead>SKU</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Quantidade</TableHead>
                <TableHead className="text-right">Custo</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {notable.map((item, index) => (
                <TableRow key={`${item.sku}-${index}`}>
                  <TableCell className="font-mono text-2xs">
                    {item.sku || "—"}
                  </TableCell>
                  <TableCell>
                    <Badge
                      variant={
                        item.status === "update"
                          ? "warning"
                          : item.status === "unknown"
                            ? "neutral"
                            : "danger"
                      }
                    >
                      {item.status === "update"
                        ? "Divergente"
                        : item.status === "unknown"
                          ? "Desconhecido"
                          : item.reason ?? "Inválido"}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right text-2xs">
                    {item.quantity
                      ? `${item.quantity.from ?? "—"} → ${item.quantity.to ?? "—"}`
                      : "—"}
                  </TableCell>
                  <TableCell className="text-right text-2xs">
                    {item.cost
                      ? `${item.cost.from ?? "—"} → ${item.cost.to}`
                      : "—"}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}

function CountChip({ label, value }: { label: string; value: number }) {
  return (
    <span className="rounded-control bg-surface-sunken px-2 py-1 text-2xs text-ink-secondary">
      <span className="font-semibold text-ink">{value}</span> {label.toLowerCase()}
    </span>
  );
}
